import { Component, ElementRef, OnInit, OnDestroy, ViewChild, AfterContentChecked, HostListener } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { Select, Store } from '@ngxs/store';
import { CommonService, Compass, CompassAnswerable, CompassAnswerableButton, CompassAssessmentQuestionAutocomplete, CompassAutocomplete, CompassAutocompleteType, CompassScaleRange, CompassStateModel, CompassStatus, CompassSummaryScaleAutocomplete, CompassTrackable, CompassTrackableIntegrationType, CompassTrackableIntegrationTypeUtil, CompassTrackableScaleAutocomplete, CompassTrackableScaleType, OntologyService, Pair, Provider, TrackableScaleRange, TrackableValuation } from 'huddyapi';
import { Observable, Subscription, timer } from 'rxjs';
import { AppComponent } from '../app.component';
import { AssessmentPreviewDialog } from '../components/assessment-preview-dialog/assessment-preview-dialog.component';
import { CompassPieceDialog } from '../components/compass-piece-dialog/compass-piece-dialog.component';
import { ConfirmationDialog, ConfirmationDialogResult } from '../components/confirmation-dialog/confirmation-dialog.component';
import { SavedGroupStatus, SavedTextField, SelectOption, SelectOptions } from '../components/multisave';
import { Util } from '../components/util';
import { ECPath, ExpandCollapseController } from '../directives/collapsibleCard';
import { SimpleAutocomplete, SimpleAutocompleteOption, SimpleAutocompleteSelection } from '../directives/simpleAutocomplete';
import { DesktopErrorHandler } from '../services/errorHandler.service';
import { Collapsible, CompassAnswerableGroup, CompassScaleRangeGroup, CompassScoreGroupGroup, CompassTrackableGroup, CompassTrackableScaleGroup, RangeID, SavedCompass, TrackableScaleRangeGroup } from './compass-form-objects';

export interface IntegrationTrackable {
  name: string;
  group: string;
  type: string;
  units: string;
  isAdditive: boolean;
  integrations: CompassTrackableIntegrationType[];
}

@Component({
  selector: 'app-compasses',
  templateUrl: './builder.component.html',
  styleUrls: ['./builder.component.scss']
})
export class BuilderComponent extends SavedGroupStatus implements OnInit, OnDestroy, AfterContentChecked {
  static FIELD_NAME_THIS: string = '__THIS__';
  static INTEGRATION_TRACKABLES: IntegrationTrackable[] = [
    {
      name: 'Calories Burned',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'kcal',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Steps Taken',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'steps',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Distance Travelled',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'kilometers',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Distance Walked/Ran',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'kilometers',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Distance Cycled',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'kilometers',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Cycling Pedaling Cadence',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'revolutions per minute',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Cycling Wheel RPM',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'revolutions per minute',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Cycling Pedal Revolutions',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'revolutions',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Cycling Wheel Revolutions',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'revolutions',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Wheelchair Pushes',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'pushes',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Swimming Stroke Count',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'strokes',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Distance Swam',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'kilometers',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Flights of Stairs Climbed',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'flights',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time Exercised',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'hours',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time Stood',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'hours',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time Sedentary',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'minutes',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time Lightly Active',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'minutes',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time Fairly Active',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'minutes',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time Very Active',
      group: 'Activity',
      type: 'EVIDENCE',
      units: 'minutes',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Heart Rate',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'beats per minute',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Low Heart Rate Events',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'events',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'High Heart Rate Events',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'events',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Irregular Heart Rhythm Events',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'events',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Resting Heart Rate',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'beats per minute',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Heart Rate Standard Deviation',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'milliseconds',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Walking Heart Rate',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'beats per minute',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Oxygen Saturation',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'percentage',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Body Temperature',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'degrees celsius',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Systolic Blood Pressure',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'mmHg',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Diastolic Blood Pressure',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'mmHg',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Respiratory Rate',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'breaths per minute',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Blood Glucose',
      group: 'Vitals',
      type: 'EVIDENCE',
      units: 'mmol/L',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Body Mass',
      group: 'Body',
      type: 'EVIDENCE',
      units: 'kilograms',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Basal Metabolic Rate',
      group: 'Body',
      type: 'EVIDENCE',
      units: 'kcal per day',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Blood Alcohol Content',
      group: 'Alcohol Consumption',
      type: 'EVIDENCE',
      units: 'percentage',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Walking Steadiness',
      group: 'Mobility',
      type: 'EVIDENCE',
      units: 'percentage',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Walking Steadiness Events',
      group: 'Mobility',
      type: 'EVIDENCE',
      units: 'events',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Walking Speed',
      group: 'Mobility',
      type: 'EVIDENCE',
      units: 'meters per second',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Walking Cadence',
      group: 'Mobility',
      type: 'EVIDENCE',
      units: 'steps per minute',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Walking Step Length',
      group: 'Mobility',
      type: 'EVIDENCE',
      units: 'centimeters',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Walking Asymmetry',
      group: 'Mobility',
      type: 'EVIDENCE',
      units: 'percentage',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Walking Double Support Percentage',
      group: 'Mobility',
      type: 'EVIDENCE',
      units: 'percentage',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Stair Ascent Speed',
      group: 'Mobility',
      type: 'EVIDENCE',
      units: 'meters per second',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Stair Descent Speed',
      group: 'Mobility',
      type: 'EVIDENCE',
      units: 'meters per second',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time In bed',
      group: 'Sleep',
      type: 'EVIDENCE',
      units: 'hours',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time Asleep',
      group: 'Sleep',
      type: 'EVIDENCE',
      units: 'hours',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time Awake',
      group: 'Sleep',
      type: 'EVIDENCE',
      units: 'hours',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.HealthKit, CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time Out Of Bed During Sleep Session',
      group: 'Sleep',
      type: 'EVIDENCE',
      units: 'hours',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time In Light Sleep',
      group: 'Sleep',
      type: 'EVIDENCE',
      units: 'hours',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time In Deep Sleep',
      group: 'Sleep',
      type: 'EVIDENCE',
      units: 'hours',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time In Rem Sleep',
      group: 'Sleep',
      type: 'EVIDENCE',
      units: 'hours',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.GoogleFit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time In Restless Sleep',
      group: 'Sleep',
      type: 'EVIDENCE',
      units: 'hours',
      isAdditive: true,
      integrations: [CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time To Fall Asleep',
      group: 'Sleep',
      type: 'EVIDENCE',
      units: 'minutes',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    },
    {
      name: 'Time To Wake Up',
      group: 'Sleep',
      type: 'EVIDENCE',
      units: 'minutes',
      isAdditive: false,
      integrations: [CompassTrackableIntegrationType.FitBit, CompassTrackableIntegrationType.Manual]
    }
  ];

  @Select(state => state.compassState) compasses$: Observable<CompassStateModel>;

  @ViewChild('infoButton', { static: false }) infoButton: MatButton;
  @ViewChild('popover', { static: false }) popover: ElementRef;
  @ViewChild('fieldInfo', { static: false }) fieldInfo: ElementRef;
  @ViewChild('scrollableContent', { static: false }) scrollableContent: ElementRef;
  @ViewChild('sectionId', { static: false }) sectionId: ElementRef;
  @ViewChild('autofocus', { static: false }) set autofocusInput(autofocusInput: ElementRef) {
    this._autofocusInput = autofocusInput;
    if(this.shouldAutofocus && autofocusInput != null) {
      this.shouldAutofocus = false;
      if(document.activeElement.id == 'huddy-compass-filter-input')
        return;
      autofocusInput.nativeElement.focus();
    }
  }

  get autofocusInput(): ElementRef {
    return this._autofocusInput;
  }

  set filterText(filterText: string) {
    if(filterText == null)
      filterText = '';
    this._filterText = filterText;
    this.normalizedFilterText = this.normalizeText(filterText);
    this.applyFilter();
  }

  get filterText(): string {
    return this._filterText;
  }

  _filterText: string;
  normalizedFilterText: string;
  savedCompassMap: Map<number, SavedCompass>;
  savedCompasses: SavedCompassListing[];
  filteredListings: SavedCompassListing[];
  controlIdMap: Map<number, SavedCompassListing>;
  otherMap: Map<number, SavedCompass>;
  private _selectedId: number;
  showingMissingSelectedDialog: number;
  missingDialogRef: MatDialogRef<ConfirmationDialog>;
  shouldAutofocus: boolean;
  private _autofocusInput: ElementRef;
  fieldInfoShowing: boolean;
  fieldInfoElem: HTMLElement;
  fieldInfoPosition: string;
  fieldInfoArrowPosition: string;
  ecController: ExpandCollapseController;
  lastSectionId: Pair<number, number>;
  shouldScrollToNew: boolean;
  trackableAutocomplete: SimpleAutocomplete<CompassTrackable | IntegrationTrackable>;
  private possibleIntegrationsMap: Map<string, CompassTrackableIntegrationType[]>;

  set selectedId(selectedId: number) {
    this._selectedId = selectedId;
    this.fieldInfoElem = null;
    this.selectionChanged();
  }

  get selectedId(): number {
    return this._selectedId;
  }

  providerMap: Map<string, Provider>;
  subscription: Subscription;
  providerOptions: SelectOptions<string>;
  infoPopoverShowing: boolean;
  infoPopoverShown: boolean;
  infoPopoverPosition: string;
  private _autocompleteTrackableFilterText: string;
  private _autocompleteTrackables: AutocompleteTrackable[];

  set autocompleteTrackableFilterText(autocompleteTrackableFilterText: string) {
    this._autocompleteTrackableFilterText = autocompleteTrackableFilterText || '';
    this.filterAutocompleteTrackables();
  }

  get autocompleteTrackableFilterText(): string {
    return this._autocompleteTrackableFilterText;
  }

  set autocompleteTrackables(autocompleteTrackables: AutocompleteTrackable[]) {
    this._autocompleteTrackables = autocompleteTrackables;
    this.filterAutocompleteTrackables();
  }

  get autocompleteTrackables(): AutocompleteTrackable[] {
    return this._autocompleteTrackables;
  }

  constructor(
    public errorHandler: DesktopErrorHandler,
    public store: Store,
    public snackbar: MatSnackBar,
    public dialog: MatDialog,
    public ontologyService: OntologyService
  ) {
    super(null);
    this.possibleIntegrationsMap = new Map<string, CompassTrackableIntegrationType[]>(BuilderComponent.INTEGRATION_TRACKABLES.map((trackable: IntegrationTrackable) => [trackable.name, trackable.integrations]));
    this.shouldScrollToNew = false;
    this.ecController = new ExpandCollapseController();
    this.lastSectionId = new Pair<number, number>(null, null);
    this.selectedId = null;
    this.trackableAutocomplete = new SimpleAutocomplete<CompassTrackable | IntegrationTrackable>('Existing', 'Integrated'); //LOOK HERE
    this.showingMissingSelectedDialog = null;
    this.missingDialogRef = null;
    this.shouldAutofocus = false;
    this.fieldInfoShowing = false;
    this.savedCompasses = [];
    this.savedCompassMap = new Map<number, SavedCompass>();
    this.controlIdMap = new Map<number, SavedCompassListing>();
    this.otherMap = new Map<number, SavedCompass>();
    this.infoPopoverShowing = false;
    this.infoPopoverShown = false;
    this.infoPopoverPosition = '';
    this.providerMap = new Map<string, Provider>();
    this.providerOptions = new SelectOptions<string>();
    this.autocompleteTrackableFilterText = '';
    this.autocompleteTrackables = [];
    this.filterText = '';
    this.filteredListings = [];
  }

  ngOnInit(): void {
    if(AppComponent.compassToCopy) {
      const copyCompass: Compass = AppComponent.compassToCopy.clone();
      AppComponent.compassToCopy = null;
      this.addSavedCompass(copyCompass, true);
    }
    this.subscription = this.compasses$.subscribe((state: CompassStateModel) => {
      this.providerMap.clear();
      console.log('providers',state.providers);
      const activeProviders: Provider[] = state.providers.filter((provider: Provider): boolean => provider.isActive(CommonService.currentUserToView));
      console.log('active providers',activeProviders);
      activeProviders.forEach((provider: Provider) => {
        if(provider.isActive(CommonService.currentUserToView)) {
          this.providerMap.set(provider.name, provider);
        }
      });
      this.providerOptions.options = activeProviders.map((provider: Provider): 
      SelectOption<string> => {
        return {
          value: provider.name,
          displayValue: provider.name
        };
      });
      let presentMap: Map<number, void> = new Map<number, void>();
      let autocompleteTrackables: AutocompleteTrackable[] = [];
      this.autocompleteTrackables = [];
   
      state.compasses.filter((compass: Compass): boolean => {
        return !compass.providerName || this.providerMap.has(compass.providerName);
      }).forEach((compass: Compass) => {
        presentMap.set(compass.id);
        if(compass.createdFrom != null && this.savedCompassMap.has(compass.createdFrom)) {
          let savedCompass: SavedCompass = this.savedCompassMap.get(compass.createdFrom);
          const wasSelected: boolean = this.isSelected(savedCompass);
          savedCompass.updateFrom(compass);
          this.savedCompassMap.set(compass.id, savedCompass);
          if(wasSelected)
            this.select(savedCompass, true);
          this.savedCompassMap.delete(compass.createdFrom);
          presentMap.set(compass.createdFrom);
        } else if(this.savedCompassMap.has(compass.id)) {
          if(this.showingMissingSelectedDialog == compass.id)
            this.missingDialogRef?.close();
          this.savedCompassMap.get(compass.id).updateFrom(compass);
        } else
          this.savedCompassMap.set(compass.id, this.constructSavedCompass(compass));
        compass.trackables.forEach((trackable: CompassTrackable) => {
          if(trackable.name?.length > 0) {
            const isEvidence: boolean = trackable.trackableType == 'EVIDENCE';
            let autocompleteTrackable: AutocompleteTrackable = new AutocompleteTrackable(new CompassTrackable({
              ...trackable,
              trackableId: null,
              accessControl: null,
              unitsOfMeasure: isEvidence ? trackable.unitsOfMeasure : null,
              normalValue: isEvidence ? trackable.normalValue : null,
              additive: isEvidence ? trackable.additive : null
            }), [compass.name]);
            const index: number = Util.sortedIndexOf(autocompleteTrackables, autocompleteTrackable, AutocompleteTrackable.compare);
            if(index < 0)
              autocompleteTrackables.splice(index * -1 - 1, 0, autocompleteTrackable);
            else {
              let names: string[] = autocompleteTrackables[index].compassNames;
              names.splice(Util.getSortedIndex(names, compass.name, (a: string, b: string): number => a.toLowerCase().localeCompare(b.toLowerCase())), 0, compass.name)
            }
          }
        });
        this.savedCompassMap.forEach(value => value.dirty = 0);
      });
      this.autocompleteTrackables = autocompleteTrackables;
      const deleted: SavedCompass[] = this.getSavedCompassList((savedCompass: SavedCompass): boolean =>
        !(savedCompass.isNew() || presentMap.has(savedCompass.id)));
      if(deleted.length > 0)
        this.removeOrUnbackSavedCompasses(deleted);
      else
        this.setSavedCompassesFromMap();
      this.updateECState();
    });
  }

  ngOnDestroy(): void {
    if(this.subscription)
      this.subscription.unsubscribe();
  }

  ngAfterContentChecked(): void {
    if(this.fieldInfoShowing && this.fieldInfoElem) {
      this.fieldInfoPosition = this.getFieldInfoPosition();
      this.fieldInfoArrowPosition = this.getFieldInfoArrowPosition();
    }
    if(this.sectionId?.nativeElement) {
      const sectionIdArr: number[] = this.sectionId.nativeElement.innerText.split(',').map((str: string): number => parseInt(str));
      if(sectionIdArr[0] != this.lastSectionId.a || sectionIdArr[1] != this.lastSectionId.b) {
        this.lastSectionId.a = sectionIdArr[0];
        this.lastSectionId.b = sectionIdArr[1];
        if(sectionIdArr[0] == this.selectedId) {
          const selected: SavedCompass = this.getSelected();
          if(selected && selected.groupIndex == sectionIdArr[1])
            this.scrollableContent?.nativeElement?.scrollTo(0, selected.scrollTops[selected.groupIndex]);
        }
      }
    }
  }

  updateScrollTop() {
    if(this.sectionId?.nativeElement) {
      const sectionIdArr: number[] = this.sectionId.nativeElement.innerText.split(',').map((str: string): number => parseInt(str));
      if(sectionIdArr[0] == this.selectedId) {
        const selected: SavedCompass = this.getSelected();
        if(selected && selected.groupIndex == sectionIdArr[1])
          selected.scrollTops[selected.groupIndex] = this.scrollableContent?.nativeElement?.scrollTop || 0;
      }
    }
  }

  updateECState() {
    const selected: SavedCompass = this.getSelected();
    this.ecController.setState(selected?.getExpandCollapseState() || {});
  }

  constructSavedCompass(compass: Compass): SavedCompass {
    return new SavedCompass(compass, this.updators, this.providerOptions, this.providerMap, (compass: Compass | SavedCompass): boolean => this.isSelected(compass), () => {
      this.shouldAutofocus = true;
      this.fieldInfoElem = null;
    }, (savedCompasses: SavedCompass[]): void => this.removeSavedCompasses(savedCompasses), (value: string) => this.autocompleteTrackableFilterText = value, this.dialog, this.snackbar, this.store, this.errorHandler, this.ontologyService, (): void => this.updateScrollTop(), this.possibleIntegrationsMap, (): void => this.updateECState());
  }

  getSavedCompassList(filter?: (savedCompass: SavedCompass) => boolean): SavedCompass[] {
    let result: SavedCompass[] = [];
    this.savedCompasses.forEach((listing: SavedCompassListing) => {
      [listing.draft, listing.published].forEach((savedCompass: SavedCompass) => {
        if(savedCompass && (!filter || filter(savedCompass))) {
          console.log('adding ', savedCompass.name, savedCompass.isDraft(), savedCompass.isSaving());
          result.push(savedCompass);
        }
      });
    });
    return result;
  }

  canDeactivate(): boolean | Promise<boolean> {
    console.log('canDeactivate builder', this.isDirty());
    if(!(this.isDirty() || this.getSavedCompassList((savedCompass: SavedCompass): boolean => savedCompass.isDraft() && savedCompass.isSaving()).length > 0))
      return true;
    return new Promise<boolean>((resolve) => {
      this.missingDialogRef?.close();
      const dialogRef = this.dialog.open(ConfirmationDialog, {
        width: '636px',
        data: {
          minWidth: '300px',
          title: 'Leave without saving?',
          body: 'Any unsaved changes will be lost forever.',
          buttons: [
            {
              text: 'Cancel',
              class: 'secondary',
              handler: (): void => {
                resolve(false);
              }
            },
            {
              text: 'Discard changes',
              class: 'danger',
              handler: (): void => {
                resolve(true);
              }
            },
            {
              text: 'Save changes',
              class: '',
              disabled: (): boolean => !this.canSaveAll(),
              handler: async (): Promise<void> => {
                return this.saveAll().then((value: boolean) => {
                  resolve(value);
                });
              }
            }
          ],
          init: async (): Promise<boolean> => {
            return Promise.all(this.getSavedCompassList((savedCompass: SavedCompass): boolean => savedCompass.isDraft() && savedCompass.isSaving()).map(
              (savedCompass: SavedCompass): Promise<boolean> => savedCompass.getPromise()
            )).then((values: boolean[]) => {
              if(values.find((value: boolean): boolean => !value) != null) {
                resolve(false);
                return false;
              }
              if(this.isDirty() || this.isInvalid() || this.hasError())
                return true;
              resolve(true);
              return false;
            });
          }
        }
      });
      dialogRef.beforeClosed().subscribe((result: ConfirmationDialogResult) => {
        if(!result)
          resolve(false);
      });
    });
  }

  canSaveAll(): boolean {
    return this.savedCompasses.findIndex((listing: SavedCompassListing) => {
      return listing.draft?.isDirty() && !listing.draft.compassSaveEnabled();
    }) < 0;
  }

  async saveAll(): Promise<boolean> {
    return Promise.all(this.getSavedCompassList((savedCompass: SavedCompass): boolean => savedCompass.isDraft() && !savedCompass.isSaving()).map(
      (savedCompass: SavedCompass): Promise<boolean> => savedCompass.save(false)
    )).then((values: boolean[]) => {
      if(values.find((value: boolean): boolean => !value) == null)
        return true;
      this.snackbar.open('Error saving compasses', 'Dismiss', {
        duration: 3000
      });
      return false;
    });
  }

  select(compass: Compass | SavedCompass, forCreate: boolean = false): void {
    const selected: SavedCompass = this.getSelected();
    if(selected && !selected.isEditing(null))
      selected.edit(null);
    this.updateScrollTop();
    this.selectedId = compass?.id;
    this.updateECState();
    if(compass && compass.status == CompassStatus.DRAFT && !forCreate)
      this.shouldAutofocus = true;
  }

  selectListing(listing: SavedCompassListing): void {
    this.select(listing.draft || listing.published);
  }

  isSelected(compass: Compass | SavedCompass): boolean {
    return this.selectedId == compass.id;
  }

  listingIsSelected(listing: SavedCompassListing): boolean {
    return (listing.draft && this.isSelected(listing.draft)) || (listing.published && this.isSelected(listing.published));
  }

  getSelected(): SavedCompass {
    return this.selectedId != null ? this.savedCompassMap.get(this.selectedId) : null;
  }

  discardChanges(compass: SavedCompass) {
    compass.reset();
    if(this.isSelected(compass))
      this.updateECState();
  }

  scrollToNew(element: ElementRef) {
    if(!this.shouldScrollToNew)
      return;
    this.shouldScrollToNew = false;
    timer(0).subscribe(() => {
      element?.nativeElement?.scrollIntoViewIfNeeded();
    });
  }

  setShouldScrollToNew(): void {
    this.shouldScrollToNew = true;
  }

  translateRangeIndex(index: number): string {
    return RangeID.fromRangeIndex(index);
  }

  addTrackable(compass: SavedCompass) {
    compass.addTrackable();
    if(this.isSelected(compass)) {
      compass.trackables[compass.trackables.length - 1].expanded = true;
      this.ecController.addStatePath(ECPath.parse('trackables'), true);
      this.setShouldScrollToNew();
    }
  }

  addDocument(compass: SavedCompass) {
    compass.addDocument();
    if(this.isSelected(compass)) {
      compass.documents[compass.documents.length - 1].expanded = true;
      this.ecController.addStatePath(ECPath.parse('documents'), true);
      this.setShouldScrollToNew();
    }
  }

  handleAutocompleteTrackableSelection(selection: SimpleAutocompleteSelection, trackable: CompassTrackableGroup): void {
    switch(selection.tab.id) {
      case this.trackableAutocomplete.getTabs()[0].id:
        this.autofillTrackable(trackable, selection.option.value as CompassTrackable);
        break;
      case this.trackableAutocomplete.getTabs()[1].id:
        this.autofillIntegratedTrackable(trackable, selection.option.value as IntegrationTrackable);
        break;
      default:
    }
  }

  autofillTrackable(trackable: CompassTrackableGroup, changeTo: CompassTrackable) {
    trackable.changeTo(changeTo);
    if(!trackable.compass || this.isSelected(trackable.compass))
      this.updateECState();
  }

  autofillIntegratedTrackable(trackable: CompassTrackableGroup, changeTo: IntegrationTrackable): void {
    trackable.setIntegration(changeTo)
  }

  removeTrackable(compass: SavedCompass, index: number) {
    compass.removeTrackable(index);
    if(this.isSelected(compass))
      this.ecController.removeStatePath(ECPath.parse('trackables[' + index + ']'));
  }

  removeDocument(compass: SavedCompass, index: number) {
    compass.removeDocument(index);
    if(this.isSelected(compass))
      this.ecController.removeStatePath(ECPath.parse('documents[' + index + ']'));
  }

  addTrackableScale(trackable: CompassTrackableGroup, index: number) {
    trackable.addScale();
    if(trackable.compass && this.isSelected(trackable.compass)) {
      trackable.scales[trackable.scales.length - 1].expanded = true;
      this.ecController.addStatePath(ECPath.parse('trackables[' + index + ']scales'), true);
      this.setShouldScrollToNew();
    }
  }

  removeTrackableScale(trackable: CompassTrackableGroup, trackableIndex: number, scaleIndex: number) {
    trackable.removeScale(scaleIndex);
    if(trackable.compass && this.isSelected(trackable.compass))
      this.ecController.removeStatePath(ECPath.parse('trackables[' + trackableIndex + ']scales[' + scaleIndex + ']'));
  }

  addAnswerable(compass: SavedCompass) {
    compass.addAnswerable();
    if(this.isSelected(compass)) {
      compass.answerables[compass.answerables.length - 1].expanded = true;
      this.ecController.addStatePath(ECPath.parse('answerables'), true);
      this.setShouldScrollToNew();
    }
  }

  removeAnswerable(compass: SavedCompass, index: number) {
    compass.removeAnswerable(index);
    if(this.isSelected(compass))
      this.ecController.removeStatePath(ECPath.parse('answerables[' + index + ']'));
  }

  async moveAnswerable(compass: SavedCompass, fromIndex: number, toIndex: number) {
    if(!compass.moveAnswerable(fromIndex, toIndex))
      return;
    if(this.isSelected(compass))
      this.updateECState();
  }

  addAnswerableButton(answerable: CompassAnswerableGroup, index: number) {
    answerable.addButton();
    if(answerable.compass && this.isSelected(answerable.compass)) {
      answerable.buttons[answerable.buttons.length - 1].expanded = true;
      this.ecController.addStatePath(ECPath.parse('answerables[' + index + ']buttons'), true);
      this.setShouldScrollToNew();
    }
  }

  removeAnswerableButton(answerable: CompassAnswerableGroup, answerableIndex: number, buttonIndex: number) {
    answerable.removeButton(buttonIndex);
    if(answerable.compass && this.isSelected(answerable.compass))
      this.ecController.removeStatePath(ECPath.parse('answerables[' + answerableIndex + ']buttons[' + buttonIndex + ']'));
  }

  addScoreGroup(compass: SavedCompass) {
    compass.addScoreGroup();
    if(this.isSelected(compass)) {
      compass.scoreGroups[compass.scoreGroups.length - 1].expanded = true;
      this.ecController.addStatePath(ECPath.parse('scoreGroups'), true);
      this.setShouldScrollToNew();
    }
  }

  removeScoreGroup(compass: SavedCompass, index: number) {
    compass.removeScoreGroup(index);
    if(this.isSelected(compass))
      this.ecController.removeStatePath(ECPath.parse('scoreGroups[' + index + ']'));
  }

  addSavedCompass(compass: Compass = null, copy: boolean = false): void {
    if(compass) {
      if(copy) {
        compass.sourceId = compass.id;
        compass.versionControlId = null;
        (compass.trackables || []).forEach((trackable: CompassTrackable) => {
          trackable.sourceCompassTrackableId = trackable.trackableId;
          trackable.trackableId = null;
        });
        compass.name = (compass.name + ' Copy').substr(0, 50);
      }
      compass.id = null;
      compass.status = CompassStatus.DRAFT;
      compass.version = copy ? 1 : (compass.version + 1);
    }
    const savedCompass: SavedCompass = this.constructSavedCompass(compass);
    if(!compass) {
      this.addAnswerable(savedCompass);
      this.addAnswerable(savedCompass);
    }
    this.savedCompassMap.set(savedCompass.id, savedCompass);
    this.setSavedCompassesFromMap(null);
    this.select(savedCompass);
  }

  removeSavedCompasses(savedCompasses: SavedCompass[]): void {
    let selectedIndex: number;
    savedCompasses.forEach((savedCompass: SavedCompass) => {
      if(this.showingMissingSelectedDialog == savedCompass.id)
        this.missingDialogRef?.close();
      if(this.isSelected(savedCompass)) {
        selectedIndex = this.getSelectedIndex();
        this.select(null);
      }
      this.savedCompassMap.delete(savedCompass.id);
      savedCompass.detach();
    });
    this.setSavedCompassesFromMap(selectedIndex);
  }

  removeOrUnbackSavedCompasses(savedCompasses: SavedCompass[]): void {
    let selectedIndex: number;
    let deleted: boolean = false;
    savedCompasses.forEach((savedCompass: SavedCompass) => {
      if(this.showingMissingSelectedDialog == savedCompass.id)
        return;
      const isSelected: boolean = this.isSelected(savedCompass);
      if(isSelected && savedCompass.isDraft() && !savedCompass.isDeleting() && this.showingMissingSelectedDialog == null) {
        this.dialog.closeAll();
        this.showingMissingSelectedDialog = savedCompass.id;
        this.missingDialogRef = this.dialog.open(ConfirmationDialog, {
          width: '636px',
          data: {
            minWidth: '300px',
            title: 'This compass no longer exists',
            body: savedCompass.getListingName() + ' seems to have been deleted.  Would you like to continue editing it?',
            buttons: [
              {
                text: 'Continue editing',
                class: 'secondary',
                handler: (): boolean => {
                  const oldId: number = savedCompass.id;
                  savedCompass.updateFrom(null);
                  this.savedCompassMap.set(savedCompass.id, savedCompass);
                  this.savedCompassMap.delete(oldId);
                  if(isSelected)
                    this.select(savedCompass, true);
                  this.setSavedCompassesFromMap();
                  return true;
                }
              },
              {
                text: 'Remove',
                class: '',
                handler: (): boolean => false
              }
            ]
          }
        });
        this.missingDialogRef.beforeClosed().subscribe((result: ConfirmationDialogResult) => {
          this.showingMissingSelectedDialog = null;
          this.missingDialogRef = null;
          if(result?.result || result?.error)
            return;
          this.removeSavedCompasses([savedCompass]);
        });
        return;
      }
      if(savedCompass.isDirty()) {
        const oldId: number = savedCompass.id;
        savedCompass.updateFrom(null);
        this.savedCompassMap.set(savedCompass.id, savedCompass);
        this.savedCompassMap.delete(oldId);
        if(isSelected)
          this.select(savedCompass, true);
      } else {
        if(isSelected) {
          selectedIndex = this.getSelectedIndex();
          this.select(null);
        }
        this.savedCompassMap.delete(savedCompass.id);
        savedCompass.detach();
        if(!deleted)
          deleted = true;
      }
    });
    if(deleted)
      this.setSavedCompassesFromMap(selectedIndex);
  }

  setSavedCompassesFromMap(selectIndex: number = 0): void {
    let drafts: SavedCompass[] = [], savedCompasses: SavedCompassListing[] = [],
      controlIdMap: Map<number, SavedCompassListing> = new Map<number, SavedCompassListing>(),
      otherMap: Map<number, SavedCompass> = new Map<number, SavedCompass>();
    Array.from(this.savedCompassMap.values()).forEach((savedCompass: SavedCompass) => {
      if(savedCompass.isDraft()) {
        drafts.push(savedCompass);
        return;
      }
      if(savedCompass.versionControlId == null)
        return;
      const existing: SavedCompassListing = controlIdMap.get(savedCompass.versionControlId);
      if(existing) {
        if(savedCompass.version > existing.published.version)
          existing.published = savedCompass;
      } else {
        const listing: SavedCompassListing = new SavedCompassListing(null, savedCompass);
        controlIdMap.set(savedCompass.versionControlId, listing);
        savedCompasses.push(listing);
      }
    });
    this.controlIdMap = controlIdMap;
    drafts.forEach((savedCompass: SavedCompass) => {
      let listing: SavedCompassListing = controlIdMap.get(savedCompass.versionControlId);
      if(listing) {
        if((listing.draft && savedCompass.version <= listing.draft.version)
          || (listing.published && savedCompass.version <= listing.published.version))
          return;
        listing.draft = savedCompass;
        otherMap.set(savedCompass.id, listing.published);
        otherMap.set(listing.published.id, savedCompass);
      } else {
        listing = new SavedCompassListing(savedCompass, null); 
        savedCompasses.push(listing);
      }
      if(savedCompass.versionControlId)
        controlIdMap.set(savedCompass.versionControlId, listing);
    });
    this.otherMap = otherMap;
    this.savedCompasses = savedCompasses.sort(SavedCompassListing.compare);
    const selected: SavedCompass = this.getSelected();
    if(selected?.versionControlId != null) {
      const listing: SavedCompassListing = controlIdMap.get(selected.versionControlId);
      if(listing) {
        const relevantCompass: SavedCompass = listing[selected.isDraft() ? 'draft' : 'published'];
        if(!this.isSelected(relevantCompass)) {
          if(selected.isDraft() && (selected.isDirty() || !selected.isEditing(null)))
            this.errorHandler.handleSimple(null, 'The draft you were editing no longer exists');
          this.select(relevantCompass);
        }
      } else
        this.select(null);
    }
    this.applyFilter(selectIndex);
  }

  normalizeText(text: string): string {
    return (text == null ? '' : text).toLowerCase().replace(/\s/g, '');
  }

  applyFilter(selectedIndex: number = 0): void {
    this.filteredListings = this.normalizedFilterText.length == 0 ? this.savedCompasses : this.savedCompasses.filter((listing: SavedCompassListing): boolean => listing.matchesNormalizedText(this.normalizedFilterText, this.normalizeText));
    this.autoSelect(selectedIndex);
  }

  autoSelect(selectIndex: number = 0): void {
    if(selectIndex == null)
      return;
    if(this.selectedId && this.savedCompassMap.has(this.selectedId)) {
      const selected: SavedCompass = this.getSelected();
      if(selected.versionControlId != null) {
        const listing: SavedCompassListing = this.controlIdMap.get(selected.versionControlId);
        if(listing?.matchesNormalizedText(this.normalizedFilterText, this.normalizeText))
          return;
      }
    }
    if(this.filteredListings.length == 0)
      this.select(null);
    else
      this.selectListing(this.filteredListings[Math.max(0, Math.min(this.savedCompasses.length - 1, selectIndex))]);
  }

  normalizeAutofillTrackableName(name: string): string {
    return (name || '').toLowerCase().replace(/\s/g, '');
  }

  updateTrackableAutocompleteOptions(filteredAutocomleteTrackables: AutocompleteTrackable[], integrationTrackableOptions: SimpleAutocompleteOption<IntegrationTrackable>[]) {
    this.trackableAutocomplete.getTabs()[0].setOptions((filteredAutocomleteTrackables || []).map((trackable: AutocompleteTrackable): SimpleAutocompleteOption => new SimpleAutocompleteOption<CompassTrackable>(trackable.trackable, trackable.trackable.name, trackable.compassNames.join(', '))));
   this.trackableAutocomplete.getTabs()[1].setOptions(integrationTrackableOptions || []); //LOOK HERE
  }

  getOptionsFromIntegrationTrackables(trackables: IntegrationTrackable[], groupName: string): SimpleAutocompleteOption<IntegrationTrackable>[] {
    return trackables.map((trackable: IntegrationTrackable): SimpleAutocompleteOption => new SimpleAutocompleteOption<IntegrationTrackable>(trackable, trackable.name, trackable.integrations.join(', '), groupName));
  }

  filterAutocompleteTrackables() {
    let filteredAutocompleteTrackables: AutocompleteTrackable[], integrationTrackableOptions: SimpleAutocompleteOption<IntegrationTrackable>[];
    const normalizedFilterText: string = this.normalizeAutofillTrackableName(this.autocompleteTrackableFilterText);
    if(!this.autocompleteTrackableFilterText)
      filteredAutocompleteTrackables = this.autocompleteTrackables;
    else {
      filteredAutocompleteTrackables = this.autocompleteTrackables.filter((trackable: AutocompleteTrackable): boolean =>
        this.normalizeAutofillTrackableName(trackable.trackable.name).includes(normalizedFilterText));
    }
    integrationTrackableOptions = (!this.autocompleteTrackableFilterText ? BuilderComponent.INTEGRATION_TRACKABLES : BuilderComponent.INTEGRATION_TRACKABLES.filter(
      (trackable: IntegrationTrackable): boolean => this.normalizeAutofillTrackableName(trackable.name).includes(normalizedFilterText)
    )).map((trackable: IntegrationTrackable): SimpleAutocompleteOption => new SimpleAutocompleteOption<IntegrationTrackable>(trackable, trackable.name, trackable.integrations.map((integration: CompassTrackableIntegrationType): string => CompassTrackableIntegrationTypeUtil.getIntegrationDisplayValue(integration)).join(', '), trackable.group));
    this.updateTrackableAutocompleteOptions(filteredAutocompleteTrackables, integrationTrackableOptions);
  }

  trackableNameFieldFocused(field: SavedTextField) {
    this.autocompleteTrackableFilterText = field.value;
  }

  addTrackableScalePiece(scale: CompassTrackableScaleGroup) {
    this.openPieceManager(CompassAutocompleteType.TRACKABLE_SCALE, null, new CompassTrackableScaleAutocomplete({
      id: null,
      name: '',
      type: CompassAutocompleteType.TRACKABLE_SCALE,
      value: scale.scaleRanges.map((range: TrackableScaleRangeGroup): TrackableScaleRange => range.getSaved(scale.getScaleType(scale.getSaved())))
    }));
  }

  addQuestionPiece(question: CompassAnswerableGroup) {
    const savedQuestion: CompassAnswerable = question.getSaved();
    savedQuestion.answers.forEach((answer: CompassAnswerableButton) => {
      if(answer.weights.length > 0) {
        answer.weights = [answer.weights[0]];
        answer.weights[0].groupGuids = [];
      }
    });
    this.openPieceManager(CompassAutocompleteType.QUESTION, null, new CompassAssessmentQuestionAutocomplete({
      id: null,
      name: '',
      type: CompassAutocompleteType.QUESTION,
      value: savedQuestion
    }));
  }

  addSummaryScalePiece(ranges: CompassScaleRangeGroup[]) {
    this.openPieceManager(CompassAutocompleteType.SUMMARY_SCALE, null, new CompassSummaryScaleAutocomplete({
      id: null,
      name: '',
      type: CompassAutocompleteType.SUMMARY_SCALE,
      value: ranges.map((range: CompassScaleRangeGroup): CompassScaleRange => range.getSaved())
    }));
  }

  openPieceManager(type?: CompassAutocompleteType, target?: CompassTrackableGroup | SavedCompass, from?: CompassAutocomplete) {
    const dialogRef: MatDialogRef<CompassPieceDialog> = this.dialog.open(CompassPieceDialog, {
      width: '880px',
      autoFocus: false,
      data: {
        type: type,
        from: from
      }
    });
    dialogRef.beforeClosed().subscribe((result: CompassAutocomplete) => {
      if(!result)
        return;
      switch(result.type) {
        case CompassAutocompleteType.TRACKABLE_SCALE:
          if(!(target instanceof CompassTrackableGroup))
            return;
          const ranges: TrackableScaleRange[] = (result as CompassTrackableScaleAutocomplete).value;
          target.addScale(new TrackableValuation({
            groupGuids: [],
            scaleType: ranges[0].type,
            scale: ranges,
            trackableName: target.name.savedValue
          }));
          if(target.compass && this.isSelected(target.compass))
            this.setShouldScrollToNew();
          break;
        case CompassAutocompleteType.SUMMARY_SCALE:
          if(!((target instanceof CompassScoreGroupGroup) || (target instanceof SavedCompass)))
            return;
          target.changeScaleTo((result as CompassSummaryScaleAutocomplete).value);
          break;
        case CompassAutocompleteType.QUESTION:
          if(!(target instanceof SavedCompass))
            return;
          target.addAnswerable((result as CompassAssessmentQuestionAutocomplete).value, true, true, true);
          if(this.isSelected(target))
            this.setShouldScrollToNew();
          break;
        default:
      }
      this.updateECState();
    });
  }

  previewAssessment(compass: SavedCompass, startIndex: number): void {
    this.dialog.open(AssessmentPreviewDialog, {
      width: '1080px',
      data: {
        assessment: compass.getSaved().assessment,
        startIndex: startIndex
      }
    });
  }

  getSelectedIndex(): number {
    let result: number = -1;
    if(this.selectedId) {
      this.savedCompasses.find((listing: SavedCompassListing, index: number): boolean => {
        if(this.listingIsSelected(listing)) {
          result = index;
          return true;
        }
        return false;
      });
    }
    return result;
  }

  selectNext(): void {
    const index = this.getSelectedIndex();
    if(index < 0)
      return;
    this.selectListing(this.savedCompasses[(index + 1) % this.savedCompasses.length]);
  }

  selectPrevious(): void {
    const index = this.getSelectedIndex();
    if(index < 0)
      return;
    this.selectListing(this.savedCompasses[index == 0 ? this.savedCompasses.length - 1 : index - 1]);
  }

  getEmptyStateTitle(): string {
    return this.savedCompasses.length == 0 ? 'No compasses' : 'No compass selected';
  }

  getEmptyStateDesc(): string {
    return this.savedCompasses.length == 0 ? 'Add a compass to begin' : 'Add or select a compass to begin';
  }

  getFieldInfoPositionInfo(): { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } {
    if(!(this.fieldInfoElem && this.fieldInfo && this.fieldInfo.nativeElement))
      return null;
    let vMode: number = 0; //-1 = above, 0 = side, 1 = below
    const position = this.fieldInfoElem.getBoundingClientRect();
    if(position.y <= 17)
      vMode = 1;
    else if(position.bottom + 17 >= window.innerHeight)
      vMode = -1;
    const popoverSpace: number = this.fieldInfo.nativeElement.offsetWidth + 20;
    let leftSpace: boolean = position.left - popoverSpace > 0, rightSpace: boolean = position.right + popoverSpace < window.innerWidth;
    if(vMode == 0 && !(leftSpace || rightSpace))
      vMode = (position.top + position.height / 2 > window.innerHeight / 2) ? -1 : 1;
    return { position: position, vMode: vMode, leftSpace: leftSpace, rightSpace: rightSpace };
  }

  getFieldInfoArrowPosition(): string {
    let top: string = '0', left: string = '0', transform: string = 'unset';
    const positionInfo: { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } = this.getFieldInfoPositionInfo();
    if(positionInfo) {
      switch(positionInfo.vMode) {
        case -1:
          top = (positionInfo.position.top - 11).toString() + 'px';
          left = (positionInfo.position.left + positionInfo.position.width / 2 - 9.5).toString() + 'px';
          transform = 'scaleY(-1)';
          break;
        case 0:
          top = (positionInfo.position.top + positionInfo.position.height / 2 - 4).toString() + 'px';
          if(positionInfo.rightSpace) {
            left = (positionInfo.position.right - 1).toString() + 'px';
            transform = 'rotate(-90deg)';
          } else {
            left = (positionInfo.position.left - 17).toString() + 'px';
            transform = 'rotate(90deg)';
          }
          break;
        case 1:
          top = (positionInfo.position.bottom + 4).toString() + 'px';
          left = (positionInfo.position.left + positionInfo.position.width / 2 - 9.5).toString() + 'px';
          break;
      }
    }
    return 'top:' + top + ';left:' + left + ';transform:' + transform + ';';
  }

  getFieldInfoPosition(): string {
    let top: string = '0', right: string = 'unset', bottom: string = 'unset', left: string = '0', maxHeight: string = 'unset';
    const positionInfo: { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } = this.getFieldInfoPositionInfo();
    if(positionInfo) {
      if(positionInfo.vMode == 0) {
        maxHeight = (window.innerHeight - 20).toString() + 'px';
        const normalTop: number = positionInfo.position.top + positionInfo.position.height / 2 - 22;
        top = Math.min(Math.max(10, window.innerHeight - this.fieldInfo.nativeElement.offsetHeight - 10), normalTop).toString() + 'px';
        if(positionInfo.rightSpace) {
          left = (positionInfo.position.right + 11).toString() + 'px';
        } else {
          left = 'unset';
          right = (window.innerWidth - (positionInfo.position.left - 11)).toString() + 'px';
        }
      } else {
        const normalLeft: number = positionInfo.position.left + positionInfo.position.width / 2 - 22;
        const rightPos: number = positionInfo.position.right - positionInfo.position.width / 2 + 22;
        left = Math.min(Math.max(Math.max(10, rightPos - this.fieldInfo.nativeElement.offsetWidth), window.innerWidth - this.fieldInfo.nativeElement.offsetWidth - 10), normalLeft).toString() + 'px';
        if(positionInfo.vMode == -1) {
          top = 'unset';
          const bottomPos: number = positionInfo.position.top - 11;
          bottom = (window.innerHeight - bottomPos).toString() + 'px';
          maxHeight = (bottomPos - 10).toString() + 'px';
        } else {
          const topNum: number = positionInfo.position.bottom + 11;
          top = topNum.toString() + 'px';
          maxHeight = (window.innerHeight - topNum - 10).toString() + 'px';
        }
      }
    }
    return 'top:' + top + ';right:' + right + ';bottom:' + bottom + ';left:' + left + ';--max-height:' + maxHeight + ';';
  }

  showFieldInfo(event: any, text: string, example?: string) {
    if(!this.fieldInfo)
      return;
    this.fieldInfo.nativeElement.getElementsByClassName('text')[0].innerText = text;
    const style: string = example?.length > 0 ? '' : 'display: none;';
    this.fieldInfo.nativeElement.getElementsByClassName('subheader')[0].style = style;
    this.fieldInfo.nativeElement.getElementsByClassName('text')[1].style = style;
    this.fieldInfo.nativeElement.getElementsByClassName('text')[1].innerText = example;
    this.fieldInfoElem = event.target;
    this.fieldInfoShowing = true;
  }

  hideFieldInfo() {
    this.fieldInfoShowing = false;
    this.fieldInfoElem = null;
  }

  ignoreKeyboardArrowEvent(event: any): boolean {
    return event.target.tagName == 'MAT-SELECT' || event.target.tagName == 'TEXTAREA';
  }

  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: any): void {
    if(this.dialog.openDialogs && this.dialog.openDialogs.length > 0)
      return;
    if(this.fieldInfoShowing) {
      event.preventDefault();
      if(event.key == 'Escape')
        this.hideFieldInfo();
      return;
    }
    switch(event.key) {
      case 's':
        if((window.navigator.platform.match("Mac") ? event.metaKey : event.ctrlKey)) {
          event.preventDefault();
          const selected: SavedCompass = this.getSelected();
          if(selected?.isEditing(null))
            this.getSelected()?.save();
        }
        break;
      case 'ArrowUp':
        if(!this.ignoreKeyboardArrowEvent(event)) {
          event.preventDefault();
          const selected: SavedCompass = this.getSelected()
          if(selected?.isEditing(null))
            this.selectPrevious();
        }
        break;
      case 'ArrowDown':
        if(!(this.ignoreKeyboardArrowEvent(event))) {
          event.preventDefault();
          const selected: SavedCompass = this.getSelected()
          if(selected?.isEditing(null))
            this.selectNext();
        }
        break;
      default:
    }
  }

  selectionChanged(): void {}

  isLoading(): boolean {
    return AppComponent.loading;
  }

  editDraft(from: SavedCompass): void {
    const other: SavedCompass = this.otherMap.get(from.id);
    if(!other) {
      this.addSavedCompass(from.getSaved());
      return;
    }
    this.select(other);
  }

  seePublished(from: SavedCompass): void {
    const other: SavedCompass = this.otherMap.get(from.id);
    if(other)
      this.select(other);
  }

  getInfoPopoverPosition() {
    let top: number = 0, left: number = 0;
    if(this.infoPopoverShown && this.popover && this.popover.nativeElement
      && this.infoButton && this.infoButton._elementRef && this.infoButton._elementRef.nativeElement) {
      const position = this.infoButton._elementRef.nativeElement.getBoundingClientRect();
      const bodyPosition = this.popover.nativeElement.parentElement.getBoundingClientRect();
      top = position.y - bodyPosition.y + 28;
      left = position.x - bodyPosition.x - 25;
    }
    return 'top:' + top + 'px;left:' + left + 'px;';
  }

  showInfoPopover() {
    if(!this.infoPopoverShown)
      this.infoPopoverShown = true;
    this.infoPopoverShowing = true;
  }

  hideInfoPopover() {
    this.infoPopoverShowing = false;
  }

  toggleCollapsed(group: Collapsible, pathString: string, forcedTo?: boolean) {
    group.expanded = forcedTo == null ? !group.expanded : forcedTo;
    this.ecController.setStateValue(ECPath.parse(pathString), group.expanded, true);
  }
}

class AutocompleteTrackable {
  trackable: CompassTrackable;
  compassNames: string[];

  constructor(trackable: CompassTrackable, compassNames?: string[]) {
    this.trackable = trackable;
    this.compassNames = compassNames || [];
  }

  equals(other: any): boolean {
    if(!(other instanceof AutocompleteTrackable))
      return false;
    return this.compareTo(other) == 0;
  }

  compareTo(other: AutocompleteTrackable): number {
    return this.trackable.compareTo(other.trackable);
  }

  static compare(a: AutocompleteTrackable, b: AutocompleteTrackable): number {
    return a.compareTo(b);
  }
}

class SavedCompassListing {
  draft: SavedCompass;
  published: SavedCompass;

  constructor(draft?: SavedCompass, published?: SavedCompass) {
    this.draft = draft;
    this.published = published;
  }

  getName(): string {
    return this.draft ? this.draft.getListingName() : (this.published ? this.published.getListingName() : 'New compass');
  }

  getDescs(): string[] {
    let result: string[] = [];
    if(this.published)
      result.push('Published version: ' + this.published.version);
    if(this.draft && !this.draft.isNew())
      result.push('Draft version: ' + this.draft.version);
    return result;
  }

  getDesc(): string {
    return this.getDescs().length > 0 ? this.getDescs()[0] : null;
  }

  getExtraDescs(): string[] {
    return this.getDescs().length > 1 ? this.getDescs().slice(1) : [];
  }

  getProvider(): Provider {
    return this.draft ? this.draft.getProvider() : (this.published ? this.published.getProvider() : null);
  }

  isDirty(): boolean {
    return this.draft ? this.draft.isDirty() : false;
  }

  hasVisibleIssue(): boolean {
    return this.draft ? this.draft.hasVisibleIssue() : false;
  }

  matchesNormalizedText(normalizedText: string, normalize: (text: string) => string): boolean {
    return this.draft?.matchesNormalizedText(normalizedText, normalize) || this.published?.matchesNormalizedText(normalizedText, normalize);
  }

  compareTo(other: SavedCompassListing): number {
    let a: SavedCompass = this.draft || this.published, b: SavedCompass = other.draft || other.published;
    if(a && b)
      return SavedCompass.compare(a, b);
    if(a)
      return -1;
    if(b)
      return 1;
    return 0;
  }

  static compare(a: SavedCompassListing, b: SavedCompassListing): number {
    return a.compareTo(b);
  }
}